import React, { useMemo } from 'react';
import { RawTrader, TraderDerivation, ProfileTabType } from './types';
import { initials, money, sMoney, sPct, TEAL, MUTE, POS, NEG, getTraderReviewPool } from './constants';

interface ProfileHeroProps {
  trader: RawTrader;
  derivation: TraderDerivation;
  riskTone: { fg: string; bg: string; bd: string };
  riskLabel: string;
  tab: ProfileTabType;
  onTabChange: (tab: ProfileTabType) => void;
  onBack: () => void;
}

export const ProfileHero: React.FC<ProfileHeroProps> = ({
  trader,
  derivation,
  riskTone,
  riskLabel,
  tab,
  onTabChange,
  onBack,
}) => {
  const { pool } = useMemo(() => getTraderReviewPool(trader), [trader]);
  const reviewCount = pool.length;

  return (
    <>
      {/* Breadcrumb Back */}
      <div className="flex items-center gap-2 text-xs md:text-sm text-[#8B95A3] mb-3.5">
        <button
          onClick={onBack}
          className="cursor-pointer text-[#0E9C9C] font-semibold bg-transparent border-0 p-0 hover:underline"
        >
          Bảng xếp hạng
        </button>
        <span className="text-[#C6CDD6]">/</span>
        <span className="text-[#16202E] font-semibold">{trader.name}</span>
      </div>

      {/* Profile Hero Card */}
      <div className="card-box overflow-hidden mb-4">
        <div className="p-4 md:p-6 flex flex-wrap gap-5">
          {/* Left Trader Identity */}
          <div className="flex gap-4 flex-1 min-w-[280px]">
            <div className="w-14 h-14 rounded-full bg-[#06223B] text-[#35D0CE] flex items-center justify-center text-lg font-bold flex-shrink-0">
              {initials(trader.name)}
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2.5 flex-wrap">
                <h1 className="text-xl md:text-2xl font-bold text-[#16202E] m-0">{trader.name}</h1>
                <div
                  style={{ background: riskTone.bg }}
                  className="flex items-center gap-1.5 py-1 px-2.5 rounded-full"
                >
                  <div style={{ background: riskTone.fg }} className="w-1.5 h-1.5 rounded-full"></div>
                  <span style={{ color: riskTone.fg }} className="text-xs font-semibold">
                    {trader.status}
                  </span>
                </div>
              </div>
              <div className="flex items-center gap-2 flex-wrap mt-2">
                <span className="text-xs font-semibold py-0.5 px-2 rounded bg-[#06223B] text-[#F1D9A8]">
                  {trader.stage}
                </span>
                <span className="text-sm text-[#5C6779]">{trader.role}</span>
                <span className="w-px h-3 bg-[#DDE3EA]"></span>
                <span className="text-xs text-[#8B95A3]">{trader.country}</span>
              </div>
              <div className="flex gap-2.5 flex-wrap mt-2.5 text-xs text-[#8B95A3]">
                {trader.tags.map((tag) => (
                  <span key={tag} className="bg-gray-100 px-2 py-0.5 rounded text-gray-600">
                    {tag}
                  </span>
                ))}
              </div>
              <div className="font-mono text-xs text-[#A6AFBC] mt-2.5">
                Giao dịch từ {trader.since} · {trader.days} ngày · {derivation.trades} lệnh
              </div>
            </div>
          </div>

          {/* Right Top Stat Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 flex-1 min-w-[280px] bg-[#FAFBFD] border border-[#EEF1F5] rounded-xl overflow-hidden">
            <div className="p-3.5">
              <div className="text-xs text-[#8B95A3] mb-1.5">Tổng vốn quản lý</div>
              <div className="font-mono text-lg font-semibold text-gray-900">{money(trader.cap)}</div>
              <div className="text-[10.5px] text-[#A6AFBC] mt-1">chưa gồm Demo {money(trader.demo)}</div>
            </div>
            <div className="p-3.5 border-l border-[#EEF1F5]">
              <div className="text-xs text-[#8B95A3] mb-1.5">Lợi nhuận tổng</div>
              <div style={{ color: trader.ret >= 0 ? POS : NEG }} className="font-mono text-lg font-semibold">
                {sPct(trader.ret)}
              </div>
              <div className="font-mono text-[10.5px] text-[#A6AFBC] mt-1">{sMoney(derivation.profit)}</div>
            </div>
            <div className="p-3.5 border-l border-[#EEF1F5]">
              <div className="text-xs text-[#8B95A3] mb-1.5">Rủi ro</div>
              <div style={{ color: riskTone.fg }} className="text-base font-bold mt-0.5">
                {riskLabel}
              </div>
              <div className="font-mono text-[10.5px] text-[#A6AFBC] mt-1">Max DD {trader.dd.toFixed(1)}%</div>
            </div>
            <div className="p-3.5 border-l border-[#EEF1F5]">
              <div className="text-xs text-[#8B95A3] mb-1.5">Trader Score</div>
              <div className="flex items-baseline gap-1">
                <span className="font-mono text-lg font-semibold text-[#16202E]">{trader.score}</span>
                <span className="font-mono text-xs text-[#A6AFBC]">/100</span>
              </div>
              <div className="h-1 bg-[#EDF0F4] rounded-full mt-2 overflow-hidden">
                <div
                  style={{ width: trader.score + '%' }}
                  className="h-full bg-[#E39A2B] rounded-full"
                ></div>
              </div>
            </div>
          </div>
        </div>

        {/* Sub-Tabs Bar */}
        <div className="px-5 flex gap-2 overflow-x-auto border-t border-[#EEF1F5]">
          {[
            ['ov', 'Tổng quan'],
            ['an', 'Phân tích'],
            ['rk', 'Rủi ro'],
            ['hs', 'Lịch sử giao dịch'],
            ['rv', `Đánh giá (${reviewCount})`],
          ].map(([k, label]) => {
            const on = tab === k;
            return (
              <button
                key={k}
                onClick={() => onTabChange(k as ProfileTabType)}
                style={{
                  color: on ? TEAL : MUTE,
                  fontWeight: on ? 600 : 500,
                  borderBottom: on ? `2px solid ${TEAL}` : '2px solid transparent',
                }}
                className="cursor-pointer py-3.5 px-4 text-sm whitespace-nowrap bg-transparent border-0 outline-none transition-colors"
              >
                {label}
              </button>
            );
          })}
        </div>
      </div>
    </>
  );
};

